import React, { useState, useEffect, useCallback } from 'react';
import { memberService } from '../../services/memberService';
import { useNotifications } from '../common/NotificationSystem';
import ProfessionalLoader from '../common/ProfessionalLoader';
import './MemberDirectory.css';

const MemberDirectory = () => {
  const { showError } = useNotifications();
  
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    state: '',
    city: '',
    format: 'json'
  });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null
  });

  const loadMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await memberService.getExternalMemberView(filters);
      
      if (response.success) {
        // Handle both array and paginated response formats
        const membersData = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setMembers(membersData);
        setPagination({
          count: response.data.count || membersData.length,
          next: response.data.next || null,
          previous: response.data.previous || null
        });
      } else {
        showError(response.message || 'Failed to load members');
      }
    } catch (error) {
      showError('An error occurred while loading members');
    } finally {
      setIsLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadMembers();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      state: '',
      city: '',
      format: 'json'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  if (isLoading) {
    return (
      <div className="member-directory">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
          <p>Loading member directory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="member-directory">
      <div className="directory-header">
        <h2>Member Directory</h2>
        <p className="directory-description">
          Browse approved AFF members and film societies
        </p>
      </div>

      {/* Filters */}
      <div className="directory-filters">
        <form onSubmit={handleSearch} className="filters-form">
          <div className="filter-group">
            <label htmlFor="search">Search</label>
            <input
              type="text"
              id="search"
              placeholder="Search by organization name, contact, or member ID..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="state">State</label>
            <select
              id="state"
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
            >
              <option value="">All States</option>
              <option value="NSW">NSW</option>
              <option value="VIC">VIC</option>
              <option value="QLD">QLD</option>
              <option value="WA">WA</option>
              <option value="SA">SA</option>
              <option value="TAS">TAS</option>
              <option value="ACT">ACT</option>
              <option value="NT">NT</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              placeholder="Enter city name..."
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            />
          </div>

          <div className="filter-actions">
            <button type="submit" className="search-btn">
              Search
            </button>
            <button type="button" onClick={clearFilters} className="clear-btn">
              Clear
            </button>
          </div>
        </form>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {members.length} of {pagination.count} members
        </p>
      </div>

      {/* Members Table */}
      <div className="members-table-container">
        {members.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>No members found</h3>
            <p>Try adjusting your search criteria or filters</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="members-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Member ID</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th>Membership Status</th>
                  <th>Active</th>
                  <th>Member Since</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.member_id} className="member-row">
                    <td className="organization-cell">
                      <div className="organization-info">
                        <div className="organization-name">
                          {member.display_name || member.organization_name}
                        </div>
                        {member.public_notes && (
                          <div className="organization-notes">
                            {member.public_notes}
                          </div>
                        )}
                        {member.website_url && (
                          <div className="organization-website">
                            <a 
                              href={member.website_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="website-link"
                            >
                              🌐 Website
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="member-id-cell">
                      <span className="member-id-badge">
                        {member.member_id}
                      </span>
                    </td>
                    <td className="contact-cell">
                      {member.contact_info && (
                        <div className="contact-info">
                          {member.contact_info.name && (
                            <div className="contact-name">
                              {member.contact_info.name}
                            </div>
                          )}
                          {member.contact_info.email && (
                            <div className="contact-email">
                              <a href={`mailto:${member.contact_info.email}`}>
                                {member.contact_info.email}
                              </a>
                            </div>
                          )}
                          {member.contact_info.phone && (
                            <div className="contact-phone">
                              📞 {member.contact_info.phone}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="location-cell">
                      <div className="location-info">
                        {member.city && (
                          <div className="location-city">{member.city}</div>
                        )}
                        {member.state && (
                          <div className="location-state">{member.state}</div>
                        )}
                        {member.country && (
                          <div className="location-country">{member.country}</div>
                        )}
                      </div>
                    </td>
                    <td className="membership-status-cell">
                      <span className={`membership-status-badge status-${member.membership_status?.toLowerCase() || 'unknown'}`}>
                        {member.membership_status || 'N/A'}
                      </span>
                    </td>
                    <td className="active-status-cell">
                      <span className={`active-status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="date-cell">
                      <div className="member-date">
                        {formatDate(member.membership_start_date)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(pagination.next || pagination.previous) && (
        <div className="pagination">
          <button 
            onClick={() => {
              // Handle previous page
              if (pagination.previous) {
                // Implementation would go here
              }
            }}
            disabled={!pagination.previous}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page 1 of {Math.ceil(pagination.count / 20)}
          </span>
          <button 
            onClick={() => {
              // Handle next page
              if (pagination.next) {
                // Implementation would go here
              }
            }}
            disabled={!pagination.next}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MemberDirectory;
